import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import UserManagement from './UserManagement';
import RoomManagement from './RoomManagement';
import GlobalSettings from './GlobalSettings';
import AdminBilling from './AdminBilling';
import Card, { CardHeader } from './ui/Card';
import StatusBanner from './ui/StatusBanner';
import {
  UserGroupIcon,
  CalendarIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  Cog6ToothIcon,
  ArrowRightIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { formatBookingDateTime, formatEndTime } from '../utils/timezone';
import { formatDuration } from '../utils/formatters';
import AdminBookingActions from './admin/AdminBookingActions';
import AnalyticsPage from './admin/analytics/AnalyticsPage';
import AdminNotifications from './admin/AdminNotifications';
import { apiErrorMessage } from '../utils/apiError';

const AdminDashboard: React.FC = () => {
  return (
    <DashboardLayout>
      <Routes>
        <Route path="/" element={<AdminHome />} />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/users/pending" element={<UserManagement />} />
        <Route path="/users/inactive" element={<UserManagement />} />
        <Route path="/rooms" element={<RoomManagement />} />
        <Route path="/billing" element={<AdminBilling />} />
        <Route path="/settings" element={<GlobalSettings />} />
        <Route path="/bookings" element={<AllBookings />} />
        <Route path="/notifications" element={<AdminNotifications />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
      </Routes>
    </DashboardLayout>
  );
};

const AdminHome: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeBookings: 0,
    totalRooms: 0,
    bookedHoursThisWeek: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true);
        const { authAPI, adminBookingAPI, roomAPI } = await import('../services/api');

        // Fetch all data in parallel
        const [users, bookings, rooms] = await Promise.all([
          authAPI.getAllUsers(),
          adminBookingAPI.getAllBookings(),
          roomAPI.getAllRooms(),
        ]);

        // Calculate active bookings (future, confirmed)
        const now = new Date();
        const activeBookings = bookings.filter(b =>
          new Date(b.start_time) > now &&
          b.status?.toLowerCase() === 'confirmed'
        );

        // Calculate booked hours this week
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay()); // Sunday
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 7);

        const bookedHoursThisWeek = bookings
          .filter(b => {
            const bookingDate = new Date(b.start_time);
            return bookingDate >= startOfWeek &&
                   bookingDate < endOfWeek &&
                   b.status?.toLowerCase() === 'confirmed';
          })
          .reduce((total, b) => total + (b.duration_minutes || 60) / 60, 0);

        setStats({
          totalUsers: users.length,
          activeBookings: activeBookings.length,
          totalRooms: rooms.length,
          bookedHoursThisWeek,
        });
      } catch (err) {
        console.error('Error fetching admin stats:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleManageUsers = () => navigate('/admin/users');
  const handleGlobalSettings = () => navigate('/admin/settings');
  const handleViewBookings = () => navigate('/admin/bookings');
  const handleManageRooms = () => navigate('/admin/rooms');
  const handleBilling = () => navigate('/admin/billing');

  // Simple utilization: booked hours / (rooms × 40 hrs/week assumed capacity)
  const weeklyCapacity = stats.totalRooms * 40;
  const utilization = weeklyCapacity > 0
    ? Math.round((stats.bookedHoursThisWeek / weeklyCapacity) * 100)
    : 0;

  return (
    <div>
      <h1 className="text-3xl font-bold text-clinic-dark mb-8 font-body">Admin Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-primary/20 rounded-full flex items-center justify-center">
              <UserGroupIcon className="h-6 w-6 text-clinic-primary" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Total Users</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {isLoading ? '--' : stats.totalUsers}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-gold/20 rounded-full flex items-center justify-center">
              <CalendarIcon className="h-6 w-6 text-clinic-gold" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Active Bookings</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {isLoading ? '--' : stats.activeBookings}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-accent/20 rounded-full flex items-center justify-center">
              <ChartBarIcon className="h-6 w-6 text-clinic-accent" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Utilization</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {isLoading ? '--%' : `${utilization}%`}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-primary-lighter rounded-full flex items-center justify-center">
              <BuildingOfficeIcon className="h-6 w-6 text-clinic-primary" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Total Rooms</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {isLoading ? '--' : stats.totalRooms}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="p-6">
        <CardHeader title="Quick Actions" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={handleManageUsers}
            className="p-4 text-left border border-clinic-primary-lighter rounded-lg hover:bg-clinic-cream/50 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <UserGroupIcon className="h-5 w-5 text-clinic-primary mr-3" />
                <div>
                  <h3 className="font-medium text-clinic-dark">Manage Users</h3>
                  <p className="text-sm text-clinic-muted">Add, edit, or remove users from the system</p>
                </div>
              </div>
              <ArrowRightIcon className="h-5 w-5 text-clinic-muted group-hover:text-clinic-primary transition-colors" />
            </div>
          </button>
          <button
            onClick={handleGlobalSettings}
            className="p-4 text-left border border-clinic-primary-lighter rounded-lg hover:bg-clinic-cream/50 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Cog6ToothIcon className="h-5 w-5 text-clinic-primary mr-3" />
                <div>
                  <h3 className="font-medium text-clinic-dark">Global Settings</h3>
                  <p className="text-sm text-clinic-muted">Configure system-wide availability and settings</p>
                </div>
              </div>
              <ArrowRightIcon className="h-5 w-5 text-clinic-muted group-hover:text-clinic-primary transition-colors" />
            </div>
          </button>
          <button
            onClick={handleViewBookings}
            className="p-4 text-left border border-clinic-primary-lighter rounded-lg hover:bg-clinic-cream/50 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CalendarIcon className="h-5 w-5 text-clinic-gold mr-3" />
                <div>
                  <h3 className="font-medium text-clinic-dark">View All Bookings</h3>
                  <p className="text-sm text-clinic-muted">Browse and manage all system bookings</p>
                </div>
              </div>
              <ArrowRightIcon className="h-5 w-5 text-clinic-muted group-hover:text-clinic-gold transition-colors" />
            </div>
          </button>
          <button
            onClick={handleManageRooms}
            className="p-4 text-left border border-clinic-primary-lighter rounded-lg hover:bg-clinic-cream/50 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <BuildingOfficeIcon className="h-5 w-5 text-clinic-primary mr-3" />
                <div>
                  <h3 className="font-medium text-clinic-dark">Manage Rooms</h3>
                  <p className="text-sm text-clinic-muted">Configure room capacity and availability</p>
                </div>
              </div>
              <ArrowRightIcon className="h-5 w-5 text-clinic-muted group-hover:text-clinic-primary transition-colors" />
            </div>
          </button>
          <button
            onClick={handleBilling}
            className="p-4 text-left border border-clinic-primary-lighter rounded-lg hover:bg-clinic-cream/50 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <BanknotesIcon className="h-5 w-5 text-clinic-gold mr-3" />
                <div>
                  <h3 className="font-medium text-clinic-dark">Billing Management</h3>
                  <p className="text-sm text-clinic-muted">View invoices and billing configuration</p>
                </div>
              </div>
              <ArrowRightIcon className="h-5 w-5 text-clinic-muted group-hover:text-clinic-gold transition-colors" />
            </div>
          </button>
        </div>
      </Card>
    </div>
  );
};

const AllBookings: React.FC = () => {
  const [bookings, setBookings] = useState<any[]>([]);
  const [rooms, setRooms] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'upcoming' | 'past' | 'cancelled'>('upcoming');
  const [clinicianFilter, setClinicianFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<'date' | 'clinician' | 'patient' | 'status' | 'duration'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const { adminBookingAPI, roomAPI } = await import('../services/api');
      const [bk, rm] = await Promise.all([adminBookingAPI.getAllBookings(), roomAPI.getAllRooms()]);
      setBookings(bk);
      setRooms((rm as any[]).map((r) => ({ id: r.id, name: r.name })));
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to fetch bookings'));
      console.error('Error fetching all bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const now = Date.now();
  const clinicianNames = Array.from(new Set(bookings.map((b) => b.clinician_name).filter(Boolean))) as string[];

  const visible = bookings
    .filter((b) => {
      if (clinicianFilter !== 'all' && b.clinician_name !== clinicianFilter) return false;
      const ts = new Date(b.start_time).getTime();
      // A within-72h cancellation converts to a self-booking: room held, billed.
      // Treat that as an active slot, not a cancellation.
      const held = b.status === 'cancelled' && b.is_self_booked;
      const active = b.status !== 'cancelled' || held;
      if (statusFilter === 'cancelled') return b.status === 'cancelled' && !b.is_self_booked;
      if (statusFilter === 'upcoming') return active && ts >= now;
      if (statusFilter === 'past') return active && ts < now;
      return true;
    })
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      let av: any; let bv: any;
      switch (sortKey) {
        case 'clinician': av = a.clinician_name || ''; bv = b.clinician_name || ''; break;
        case 'patient': av = a.patient_name || ''; bv = b.patient_name || ''; break;
        case 'status': av = a.status || ''; bv = b.status || ''; break;
        case 'duration': av = a.duration_minutes || 0; bv = b.duration_minutes || 0; break;
        default: av = new Date(a.start_time).getTime(); bv = new Date(b.start_time).getTime();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });

  if (loading) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-clinic-dark mb-8 font-body">All Bookings</h1>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clinic-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-clinic-dark mb-8 font-body">All Bookings</h1>
        <StatusBanner
          variant="error"
          title="Unable to load bookings"
          message={error}
        />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-clinic-dark mb-8 font-body">All Bookings</h1>
      <Card className="p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          {[
            { label: 'Status', value: statusFilter, set: (v: string) => setStatusFilter(v as any), opts: [['all', 'All'], ['upcoming', 'Upcoming'], ['past', 'Past'], ['cancelled', 'Cancelled']] },
            { label: 'Clinician', value: clinicianFilter, set: (v: string) => setClinicianFilter(v), opts: [['all', 'All'], ...clinicianNames.map((n) => [n, n] as [string, string])] },
            { label: 'Sort by', value: sortKey, set: (v: string) => setSortKey(v as any), opts: [['date', 'Date'], ['clinician', 'Clinician'], ['patient', 'Patient'], ['status', 'Status'], ['duration', 'Duration']] },
          ].map((f) => (
            <div key={f.label}>
              <label className="block text-xs text-clinic-muted mb-1">{f.label}</label>
              <select value={f.value} onChange={(e) => f.set(e.target.value)}
                className="text-sm border border-clinic-primary-lighter rounded-input px-2 py-1.5 bg-white">
                {f.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          ))}
          <button onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            className="text-sm text-clinic-primary font-medium px-2 py-1.5">
            {sortDir === 'asc' ? '↑ Ascending' : '↓ Descending'}
          </button>
          <span className="text-xs text-clinic-muted ml-auto self-center">{visible.length} of {bookings.length}</span>
        </div>
      </Card>
      <Card className="p-6">
        {visible.length === 0 ? (
          <div className="text-center py-8">
            <CalendarIcon className="h-12 w-12 text-clinic-muted mx-auto mb-3" />
            <p className="text-clinic-dark font-medium">No bookings found</p>
            <p className="text-sm text-clinic-muted mt-1">Try adjusting the filters above</p>
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map((booking) => (
              <div key={booking.id} className="border border-clinic-primary-lighter rounded-lg p-4 hover:bg-clinic-cream/30 transition-colors">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-clinic-dark">
                      {booking.patient_name || 'Unknown Client'} → {booking.clinician_name || 'Unknown Clinician'}
                    </h3>
                    {(() => {
                      const { date, time } = formatBookingDateTime(booking.start_time);
                      const buffer = booking.buffer_minutes || 0;
                      const endTimeStr = formatEndTime(booking.start_time, booking.duration_minutes + buffer);
                      return (
                        <>
                          <p className="text-sm text-clinic-muted">{date}</p>
                          <p className="text-sm text-clinic-muted">{time} - {endTimeStr}</p>
                        </>
                      );
                    })()}
                    {booking.room_name && (
                      <p className="text-sm text-clinic-muted">Room: {booking.room_name}</p>
                    )}
                    {booking.notes && (
                      <p className="text-sm text-clinic-muted mt-2 italic">"{booking.notes}"</p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="flex flex-col items-end gap-1">
                      {booking.is_self_booked && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-clinic-gold/20 text-clinic-brown">
                          Self-Booked
                        </span>
                      )}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        booking.status === 'confirmed'
                          ? 'bg-clinic-primary/20 text-clinic-primary'
                          : booking.status === 'cancelled'
                          ? 'bg-clinic-accent/20 text-clinic-accent'
                          : 'bg-clinic-muted/20 text-clinic-muted'
                      }`}>
                        {booking.is_self_booked && booking.status === 'cancelled' ? 'room held' : booking.status}
                      </span>
                    </div>
                    <p className="text-sm text-clinic-muted mt-1">
                      {formatDuration(booking.duration_minutes)}
                      {booking.buffer_minutes > 0 ? ` (+${booking.buffer_minutes} min buffer)` : ''}
                    </p>
                    <p className="text-xs text-clinic-muted mt-1">
                      ID: {booking.id}
                    </p>
                  </div>
                </div>
                <AdminBookingActions booking={booking} rooms={rooms} onChanged={fetchAll} />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminDashboard;
